import type { UserResource } from '@/types/users'

/** The name to show for a user: their full name, else @username, else their email. */
export function displayNameFor(user: UserResource | null | undefined) {
  if (!user) return ''
  const { first_name, last_name, username, email } = user.attributes
  const fullName = [first_name, last_name].filter(Boolean).join(' ').trim()
  return fullName || (username ? `@${username}` : '') || email
}

/** Usernames are stored lowercase without the @, as the API normalizes them. */
export function normalizeUsername(value: string) {
  return value.trim().replace(/^@/, '').toLowerCase()
}

/**
 * What a person's avatar tone is keyed on (`Avatar`'s `toneKey`), the same on every surface that shows them: their
 * username, the one column every payload carries (a chat author's and a face's `handle`, a person host's `handle`, the
 * signed-in account's `username`), else their user id. The stage payload has no user id for its host, so the username
 * comes first until it does (follow-up: then key on the id everywhere). Null without either: the neutral disc.
 */
export function personToneKey({ handle, id }: { handle?: string | null; id?: string | null }): string | null {
  return (handle && normalizeUsername(handle)) || id || null
}

/** The API's username rule (`User::USERNAME_FORMAT`, 3 to 30 characters) and its messages (`config/locales/en.yml`). */
export const USERNAME_FORMAT = /^[a-z0-9_]+$/
export const USERNAME_MIN_LENGTH = 3
export const USERNAME_MAX_LENGTH = 30
export const USERNAME_CHARACTERS_MESSAGE = 'Username can only use lowercase letters, numbers and underscores'
export const USERNAME_LENGTH_MESSAGE = 'Username must be 3 to 30 characters'

/** The API's username rule, checked before saving so most mistakes show without a round trip. */
export function usernameProblem(value: string) {
  const username = normalizeUsername(value)
  if (!username) return null
  if (username.length < USERNAME_MIN_LENGTH || username.length > USERNAME_MAX_LENGTH) return USERNAME_LENGTH_MESSAGE
  if (!USERNAME_FORMAT.test(username)) return USERNAME_CHARACTERS_MESSAGE
  return null
}
